import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BackgroundPattern } from '@/components/ui/background-pattern';
import { Button } from '@/components/ui/button';

const Home: React.FC = () => {
  const navigate = useNavigate();

  const handleTileClick = (path: string) => {
    navigate(path);
  };

  return (
    <div className="relative min-h-screen">
      <BackgroundPattern />
      <div className="relative">
        <main className="min-h-screen bg-transparent">
          <div className="container mx-auto py-8 px-4">
            <h1 className="text-5xl font-extrabold mb-6 tracking-tight text-center">
              <span className="bg-gradient-to-r from-indigo-600 via-blue-600 to-indigo-400 bg-clip-text text-transparent">
                Welcome to ICanPitch
              </span>
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto text-center leading-relaxed mb-12">
              Your complete fundraising toolkit. Select a service to get started.
            </p>

            <div className="flex flex-col items-center">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full max-w-5xl mx-auto">
                {/* Funding Prep Tile */}
                <div 
                  onClick={() => handleTileClick('/dashboard')}
                  className="group relative overflow-hidden rounded-xl bg-white/10 backdrop-blur-lg border border-white/20 shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105 cursor-pointer"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/20 to-blue-500/20 opacity-50 group-hover:opacity-70 transition-opacity" />
                  <div className="p-8 relative z-10">
                    <div className="w-16 h-16 bg-gradient-to-r from-indigo-600 to-blue-500 rounded-full flex items-center justify-center mb-6 mx-auto shadow-lg">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-center mb-3 text-gray-900 dark:text-white">Fundraising Support</h3>
                    <p className="text-gray-600 dark:text-gray-300 text-center mb-6">
                      AI-powered analysis of your pitch deck with intelligent agents that provide valuation estimates and investor-ready insights.
                    </p>
                    <div className="flex justify-center">
                      <Button variant="default" className="w-full">
                        Get Started
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Data Room Setup Tile */}
                <div 
                  onClick={() => handleTileClick('/dataroom')}
                  className="group relative overflow-hidden rounded-xl bg-white/10 backdrop-blur-lg border border-white/20 shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105 cursor-pointer"
                >
                  <div className="absolute top-0 right-0 z-20">
                    <div className="bg-indigo-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg rounded-tr-lg shadow-md transform rotate-0">
                      Experimental
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-pink-500/20 opacity-50 group-hover:opacity-70 transition-opacity" />
                  <div className="p-8 relative z-10">
                    <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-pink-500 rounded-full flex items-center justify-center mb-6 mx-auto shadow-lg">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-center mb-3 text-gray-900 dark:text-white">Investor Due Diligence</h3>
                    <p className="text-gray-600 dark:text-gray-300 text-center mb-6">
                      AI-powered Dataroom agent that leverages Daytona's virtual environment to create and manage startup files needed for investor due diligence.
                    </p>
                    <div className="flex justify-center">
                      <Button variant="default" className="w-full">
                        Get Started
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Bottom row with 1 centered tile */}
              <div className="grid grid-cols-1 gap-8 w-full max-w-3xl mx-auto mt-8">
                {/* Term Sheet Negotiation Tile */}
                <div 
                  className="group relative overflow-hidden rounded-xl bg-white/10 backdrop-blur-lg border border-white/20 shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105 cursor-pointer"
                >
                  <div className="absolute top-0 right-0 z-20">
                    <div className="bg-indigo-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg rounded-tr-lg shadow-md transform rotate-0">
                      Coming Soon
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 opacity-50 group-hover:opacity-70 transition-opacity" />
                  <div className="p-8 relative z-10">
                    <div className="w-16 h-16 bg-gradient-to-r from-emerald-600 to-teal-500 rounded-full flex items-center justify-center mb-6 mx-auto shadow-lg">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-center mb-3 text-gray-900 dark:text-white">Deal Optimization</h3>
                    <p className="text-gray-600 dark:text-gray-300 text-center mb-6">
                      AI-driven term sheet analysis and negotiation assistance with intelligent agents that identify favorable terms and potential pitfalls.
                    </p>

                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Home;
